import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

interface FeatureFlagResponse {
  org_id: string;
  feature_name: string;
  enabled: boolean;
}

// Module-level cache so consumers re-mounting or remounting components
// across the page never re-fire the network call for the same (org, flag).
// Errors are cached too (resolved to `false`) so a 400/403/etc. doesn't
// turn into a refetch storm — we hit the endpoint at most once per pair
// per page session.
const flagCache = new Map<string, Promise<boolean>>();
const warnedKeys = new Set<string>();

export const getFeatureFlag = async (
  orgId: string,
  featureName: string,
): Promise<boolean> => {
  const key = `${orgId}:${featureName}`;
  const cached = flagCache.get(key);
  if (cached) return cached;

  const promise = (async () => {
    try {
      const { data } = await remiApi.get<FeatureFlagResponse>(
        `/internal/feature-flags/${orgId}/${featureName}`,
      );
      return data.enabled;
    } catch (err) {
      // Treat any failure (unknown flag, 403, network) as "off". Warn once.
      if (!warnedKeys.has(key)) {
        warnedKeys.add(key);
        console.warn(
          `[featureFlag] ${featureName} unavailable for org ${orgId} — defaulting to off`,
          err,
        );
      }
      return false;
    }
  })();
  flagCache.set(key, promise);
  return promise;
};

export const setFeatureFlag = async (
  orgId: string,
  featureName: string,
  enabled: boolean,
): Promise<boolean> => {
  const { data } = await remiApi.put<FeatureFlagResponse>(
    `/internal/feature-flags/${orgId}/${featureName}`,
    { enabled },
  );
  // Bust the read cache so the next `getFeatureFlag` reflects the new value.
  flagCache.delete(`${orgId}:${featureName}`);
  warnedKeys.delete(`${orgId}:${featureName}`);
  return data.enabled;
};
